import { v4 as genUuid } from "uuid";
import { ContentType } from "@thenounproject/lingo-core";
import { Inspectable } from "@constants/Inspector";

import { type Upload, type UploadAction } from "@actions/uploads";
import { createAsyncAction } from "../actionCreators";
import { fetchContentTypes } from "../useFetchContentTypes";
import { enqueueUpload } from "../uploads/enqueueUpload";
import { AssetSourceVendors, ExternalSource } from "../items/useCreateAssetsFromSource";
import { processExternalFile } from "./processExternalFile";

type Args = {
  inspectable: Inspectable;
  source: ExternalSource;
  productId?: string;
};

const [useReplaceAssetFileFromSource, replaceAssetFileFromSource] = createAsyncAction(
  "assets/replaceFileFromSource",
  async ({ inspectable, source, productId }: Args, thunkApi) => {
    const contentTypes = await fetchContentTypes.lazy(thunkApi, {
      itemType: inspectable.item?.type,
      assetType: inspectable.asset?.type,
    });

    const uploadAction = generateUploadAction(source, inspectable, contentTypes, productId);
    thunkApi.dispatch(enqueueUpload(uploadAction));
  }
);

export default useReplaceAssetFileFromSource;
export { replaceAssetFileFromSource };

type ReplaceAssetPayload = {
  source?: string;
  type?: string;
  source_url?: string;
  source_headers?: Record<string, string>;
  meta: {
    figma?: { url: string };
    font?: { font_name?: string; extension?: string };
  };
};

const generateUploadAction = (
  source: ExternalSource,
  inspectable: Inspectable,
  contentTypes: ContentType[],
  productId?: string
): UploadAction => {
  if (!inspectable?.asset) {
    throw new Error("Cannot replace asset file: no asset found in inspectable");
  }

  const upload: Upload = {
    id: genUuid().toUpperCase(),
    status: "pending",
    type: source.source,
    name: source.name,
    size: source.fileSize ?? 0,
    // Provide an insertPosition for the upload modal details
    // We aren't inserting but we need it for display purposes
    insertPosition: {
      kitId: inspectable.item?.kitUuid,
      sectionId: inspectable.item?.sectionUuid,
      spaceId: inspectable.item?.spaceId ?? inspectable.asset?.spaceId,
      productId,
    },
    isAsset: !inspectable.item?.type || inspectable.item?.type === "asset",
  };

  const asset: ReplaceAssetPayload = {
    meta: {},
  };

  switch (source.source) {
    case AssetSourceVendors.Figma:
      asset.source = source.source;
      asset.type = "SVG";
      upload.type = "Figma asset";
      upload.name = source.name;
      if (source.url) {
        asset.meta.figma = { url: source.url };
      }
      break;
    case AssetSourceVendors.GoogleDrive:
      upload.type = "Google Drive";
      processExternalFile(source, upload, asset, contentTypes);
      if (source.authToken) {
        asset.source_headers = { Authorization: `Bearer ${source.authToken}` };
      }
      break;
    case AssetSourceVendors.Dropbox:
      upload.type = "Dropbox";
      processExternalFile(source, upload, asset, contentTypes);
      break;
  }

  return {
    upload,
    requestData: {
      data: asset,
      endpoint: `assets/${inspectable.asset.uuid}`,
      method: "PUT",
    },
  };
};
